import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { logger } from '@/utils/logger';
import { captureError } from '@/debug/debugStore';
import { mapProfileToFormData, defaultTeachingStyle, createEmptySubjectCard, normalizeExamResults, type ProfileLike } from '@/utils/mapTutorProfileToFormData';
import { ChevronDown, Save, AlertCircle, Loader2, Camera, XCircle, Clock, X } from 'lucide-react';
import { useAuthContext } from '@/contexts/useAuthContext';
import { uploadTutorFile } from '@/api/services/profilesApi';
import MultiSelect from '@/components/common/MultiSelect';
import SearchableDropdown from '@/components/common/SearchableDropdown';
import SearchableInput from '@/components/common/SearchableInput';
import TuitionTypeOptionContent from '@/components/common/TuitionTypeOptionContent';
import WeeklyTimeTable from '@/components/common/WeeklyTimeTable';
import TabButtonGroup from '@/components/common/TabButtonGroup';
import SingleRangeSlider from '@/components/common/SingleRangeSlider';
import Card from '@/components/common/Card';
import PastPublicExamResults from '@/components/common/PastPublicExamResults';
import { BirthdayPicker } from '@tutor/ui';
import LevelMultiSelect from '@/components/Registration/LevelMultiSelect';
import {
  TEACHING_LANGUAGES,
  CURRENT_STATUS_OPTIONS,
  EDUCATION_LEVELS_HIGHEST,
  MAJOR_OPTIONS,
  TUTOR_EXPERIENCE_TYPES,
  hasTeacherRelatedExperience,
  TUITION_EXPERIENCE_YEARS,
  TEACHING_STYLE_OPTIONS,
  UNIVERSITIES,
  GENDER_OPTIONS,
  NATIONALITIES,
} from '@/constants/dropdownOptions';
import { Plus, Trash2 } from 'lucide-react';
import type { MultiLevelSubjectCard, ExamResult, TutorRegistrationFormData } from '@/types';
import type { UpdateTutorProfileRequest, SubjectCard } from '@/types/api';
import {
  EDUCATION_LEVEL_LABELS,
  EDUCATION_SYSTEM_OPTIONS,
  getSubjectsForLevels,
  type EducationLevel,
  STATE_OPTIONS,
  STATE_DISTRICTS,
} from '@tutor/types';
import {
  stepSchemas,
  tutorExperienceSchema,
  publicExamResultsSchema,
  teachingStyleAndAboutMeSchema,
} from '@/schemas/tutorRegistrationSchemas';
import { tutorProfileSchema } from '@/schemas/authSchemas';
import { updateTutorProfile } from '@/api/services/profilesApi';
import { useI18n } from '@/i18n';
import { useNavigate } from '@/i18n/navigation';
import { localizeOptions, localizeStringOptions, localizeText } from '@/i18n/options';
import { getWeeklyTimeTableI18nProps } from '@/utils/weeklyTimeTableI18n';

const AreaNoteInputEdit: React.FC<{ onAdd: (note: string) => void; isZh: boolean }> = ({ onAdd, isZh }) => {
  const [input, setInput] = useState('');
  const commit = () => {
    const trimmed = input.trim();
    if (trimmed) { onAdd(trimmed); setInput(''); }
  };
  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        maxLength={80}
        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
        placeholder={isZh ? '例如：Taman Desa、Bangsar Baru、Sri Petaling...' : 'e.g. Taman Desa, Bangsar Baru, Sri Petaling...'}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
      />
      <button
        type="button"
        onClick={commit}
        className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
      >
        {isZh ? '添加' : 'Add'}
      </button>
    </div>
  );
};

const steps = [
  { id: 0, name: 'Personal Info', label: 'Personal Information' },
  { id: 1, name: 'Tuition Type', label: 'Type of Tuition' },
  { id: 2, name: 'Teaching Area', label: 'Teaching Areas' },
  { id: 3, name: 'Subjects by Level', label: 'Subjects & Levels' },
  { id: 4, name: 'Education', label: 'Education Information' },
  { id: 5, name: 'Teaching Time', label: 'Teaching Schedule' },
  { id: 6, name: 'Languages & Rate', label: 'Hourly Rate' },
  { id: 7, name: 'Experience', label: 'Tutor Experience' },
  { id: 8, name: 'Exam Results', label: 'Public Exam Results' },
  { id: 9, name: 'Teaching Style', label: 'Teaching Style' },
  { id: 10, name: 'About Me', label: 'About Me' },
];

const SUBJECTS_STEP_ID = 3;

// Map visual step IDs to their validation schemas
const STEP_VALIDATION_MAPPING: Record<number, z.ZodType<any>> = {
  0: tutorProfileSchema, // Personal Information
  1: stepSchemas[0], // Tuition Type
  2: stepSchemas[1], // Teaching Area
  3: stepSchemas[2], // Subjects by Level
  4: stepSchemas[3], // Education
  5: stepSchemas[4], // Teaching Time (Availability)
  6: stepSchemas[5], // Languages & Rate (Hour Rate)
  7: tutorExperienceSchema, // Experience
  8: publicExamResultsSchema, // Exam Results
  9: teachingStyleAndAboutMeSchema, // Teaching Style & About Me
  // NOTE: Step 10 (About Me) is handled separately since it's merged with Teaching Style
};

// Helper function to extract field errors from ZodError
const extractFieldErrors = (zodError: z.ZodError): Record<string, string> => {
  const fieldErrors: Record<string, string> = {};

  zodError.issues.forEach((issue) => {
    const fieldPath = issue.path.join('.');
    if (fieldPath && !fieldErrors[fieldPath]) {
      fieldErrors[fieldPath] = issue.message;
    }
  });

  return fieldErrors;
};

const createEmptyExamResult = (): ExamResult => ({
  id: Date.now().toString(),
  examType: '',
  year: new Date().getFullYear(),
  results: [],
});

const normalizeFormDataForValidation = (data: TutorRegistrationFormData) => ({
  ...data,
  examResults: normalizeExamResults(data.examResults || []),
});

// Expand multi-level cards to individual subject cards per level
const expandToSubjectCards = (multiLevelCards: MultiLevelSubjectCard[]): SubjectCard[] => {
  return multiLevelCards
    .flatMap(({ educationSystem, educationLevels, subjects }) =>
      (educationLevels as EducationLevel[]).map((level: EducationLevel) => ({
        educationSystem,
        educationLevel: level,
        subjects: subjects.filter((s) => getSubjectsForLevels([level]).includes(s)),
      }))
    )
    .filter((card) => card.subjects.length > 0);
};

const deriveWillingToTravel = (tuitionType: TutorRegistrationFormData['tuitionType']) =>
  tuitionType === 'physical' || tuitionType === 'both';

// Transform form data to API payload
const transformFormDataToApiPayload = (formData: TutorRegistrationFormData): UpdateTutorProfileRequest => {
  const transformSubjectCards = expandToSubjectCards(formData.subjectCards as MultiLevelSubjectCard[]);

  return {
    fullName: formData.fullName,
    phone: formData.phone,
    nationality: formData.nationality,
    dateOfBirth: formData.dateOfBirth,
    gender: formData.gender as 'male' | 'female' | 'other',
    tuitionType: formData.tuitionType as 'physical' | 'online' | 'both',
    preferredState: formData.preferredState || undefined,
    preferredDistricts: formData.preferredDistricts,
    preferredAreaNotes: formData.preferredAreaNotes,
    willingToTravel: deriveWillingToTravel(formData.tuitionType),
    maxTravelDistance: formData.maxTravelDistance,
    subjectCards: transformSubjectCards,
    highestEducationLevel: formData.highestEducationLevel === 'others' && formData.otherEducationLevel ? formData.otherEducationLevel : formData.highestEducationLevel,
    school: formData.school,
    teachingInstitution: formData.teachingInstitution,
    major: formData.major,
    availability: formData.availability,
    teachingLanguages: formData.teachingLanguages,
    customLanguage: formData.teachingLanguages.includes('other') ? formData.customLanguage : '',
    hourlyRate: formData.hourlyRate ? Number(formData.hourlyRate) : undefined,
    yearsOfTuitionExperience:
      formData.yearsOfTuitionExperience === '' || formData.yearsOfTuitionExperience === undefined
        ? undefined
        : Number(formData.yearsOfTuitionExperience),
    tutorExperienceTypes: formData.tutorExperienceTypes,
    examResults: hasTeacherRelatedExperience(formData.tutorExperienceTypes) ? [] : formData.examResults,
    teachingStyle: typeof formData.teachingStyle === 'object' && !Array.isArray(formData.teachingStyle)
      ? { ...defaultTeachingStyle, ...formData.teachingStyle }
      : defaultTeachingStyle,
    aboutMe: formData.aboutMe,
  };
};

const transformSubjectCardsOnlyPayload = (formData: TutorRegistrationFormData): UpdateTutorProfileRequest => ({
  subjectCards: expandToSubjectCards(formData.subjectCards as MultiLevelSubjectCard[]),
});

const EditTutorProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { locale } = useI18n();
  const isZh = locale === 'zh-CN';
  const weeklyTimeTableI18n = getWeeklyTimeTableI18nProps(locale);
  const localizedCurrentStatusOptions = localizeOptions(locale, CURRENT_STATUS_OPTIONS);
  const localizedEducationLevelsHighestOptions = localizeOptions(locale, EDUCATION_LEVELS_HIGHEST);
  const localizedTeachingLanguageOptions = localizeOptions(locale, TEACHING_LANGUAGES);
  const localizedTeachingStyleIAmOptions = localizeStringOptions(locale, TEACHING_STYLE_OPTIONS['I am']);
  const localizedTeachingStyleICanOptions = localizeStringOptions(locale, TEACHING_STYLE_OPTIONS['I can']);
  const localizedTeachingStyleIKnowOptions = localizeStringOptions(locale, TEACHING_STYLE_OPTIONS['I know']);
  const localizedEducationLevelLabels = Object.fromEntries(
    Object.entries(EDUCATION_LEVEL_LABELS).map(([key, value]) => [key, localizeText(locale, value)])
  );
  const { profile, backendUser, updateProfile } = useAuthContext();
  const isVerifiedTutor = profile?.verificationStatus === 'approved';

  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set([0]));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [localAvatarUrl, setLocalAvatarUrl] = useState<string | null>(null);
  const [formData, setFormData] = useState<TutorRegistrationFormData>({
    loginMethod: '',
    fullName: '',
    dateOfBirth: '',
    gender: '',
    nationality: '',
    occupation: '',
    tuitionType: '',
    preferredState: '',
    preferredDistricts: [],
    preferredAreaNotes: [],
    willingToTravel: false,
    maxTravelDistance: '',
    subjectCards: [createEmptySubjectCard()],
    currentStatus: '',
    highestEducationLevel: '',
    otherEducationLevel: '',
    school: '',
    teachingInstitution: '',
    major: '',
    availability: {},
    availabilityTimeRanges: {},
    teachingLanguages: [],
    customLanguage: '',
    hourlyRate: '',
    yearsOfTuitionExperience: '',
    tutorExperienceTypes: [],
    examResults: [],
    teachingStyle: defaultTeachingStyle,
    aboutMe: '',
    agreedToTerms: true,
    phone: '',
  });

  // Initialize form with existing profile data
  useEffect(() => {
    if (!backendUser || profile?.role !== 'tutor') {
      navigate('/');
      return;
    }

    // Pre-fill form with existing profile data
    if (profile) {
      const mappedFormData = mapProfileToFormData(profile as ProfileLike, formData);
      setFormData(mappedFormData);
      // Seed avatar from persisted profile
      const existingAvatar = profile.profilePhotoUrl || profile.profilePicture;
      if (existingAvatar) setLocalAvatarUrl(existingAvatar);
    }

    setIsLoading(false);
  }, [backendUser, profile, navigate]);



  // Toggle step expansion
  const toggleStep = (stepId: number) => {
    const newExpandedSteps = new Set(expandedSteps);
    if (newExpandedSteps.has(stepId)) {
      newExpandedSteps.delete(stepId);
    } else {
      newExpandedSteps.add(stepId);
    }
    setExpandedSteps(newExpandedSteps);
  };

  // Validate step
  const validateStep = (step: number): boolean => {
    const schema = STEP_VALIDATION_MAPPING[step];
    
    // Skip validation if no schema mapped for this step
    if (!schema) {
      return true;
    }

    // For personal info step, validate using tutorProfileSchema
    if (step === 0) {
      const result = schema.safeParse({
        fullName: formData.fullName,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        nationality: formData.nationality,
        phone: formData.phone,
      });
      if (!result.success) {
        const fieldErrors = extractFieldErrors(result.error);
        setErrors(fieldErrors);
        return false;
      }
    } else {
      // For other steps, validate the full form data
      const dataToValidate = normalizeFormDataForValidation(formData);
      const result = schema.safeParse(dataToValidate);
      if (!result.success) {
        const fieldErrors = extractFieldErrors(result.error);
        setErrors(fieldErrors);
        return false;
      }
    }

    // All validations passed
    setErrors({});
    return true;
  };

  // Handle save
  const handleSave = async () => {
    if (isVerifiedTutor) {
      if (!validateStep(SUBJECTS_STEP_ID)) {
        toast.error(localizeText(locale, 'Please fix your subjects and levels before saving.'));
        return;
      }
    } else {
      // Validate all steps
      let hasErrors = false;
      for (let i = 0; i < steps.length - 1; i++) { // -1 because last step is optional
        if (!validateStep(i)) {
          hasErrors = true;
          break; // Stop at the first step that fails and show its errors
        }
      }

      if (hasErrors) {
        toast.error(localizeText(locale, 'Please fix all errors before saving'));
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const isResubmitting = profile?.verificationStatus === 'rejected';
      const payload = isVerifiedTutor
        ? transformSubjectCardsOnlyPayload(formData)
        : transformFormDataToApiPayload(formData);
      const updatedProfile = await updateTutorProfile(payload);
      updateProfile({
        ...updatedProfile,
        role: profile?.role || 'tutor',
      });

      if (isVerifiedTutor) {
        toast.success(localizeText(locale, 'Subjects and levels updated successfully!'));
      } else if (isResubmitting) {
        toast.success(localizeText(locale, 'Profile updated! Your profile has been resubmitted for verification.'));
      } else {
        toast.success(localizeText(locale, 'Profile updated successfully!'));
      }
      // Stay on the same page after saving
    } catch (error) {
      logger.error('EditTutorProfilePage', 'Failed to update profile', error);
      captureError('EditTutor:submit', error);
      const errorMessage = error instanceof Error ? error.message : localizeText(locale, 'Failed to update profile. Please try again.');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Error message component
  const ErrorMessage = ({ fieldName }: { fieldName: string }) => {
    const error = errors[fieldName];
    if (!error) return null;
    return (
      <div className="flex items-start gap-2 text-red-600 text-sm mt-1">
        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>{error}</span>
      </div>
    );
  };

  // Subject card helpers
  const addSubjectCard = () => {
    const newCard: MultiLevelSubjectCard = {
      id: Date.now().toString(),
      educationSystem: '',
      educationLevels: [],
      subjects: [],
    };
    setFormData({
      ...formData,
      subjectCards: [...formData.subjectCards, newCard],
    });
  };

  const removeSubjectCard = (id: string) => {
    setFormData({
      ...formData,
      subjectCards: formData.subjectCards.filter((card) => card.id !== id),
    });
  };

  const updateSubjectCard = (id: string, updates: Partial<MultiLevelSubjectCard>) => {
    setFormData({
      ...formData,
      subjectCards: formData.subjectCards.map((card) =>
        card.id === id ? { ...card, ...updates } : card
      ),
    });
  };

  // Exam result helpers
  const addExamResult = () => {
    const newExam = createEmptyExamResult();
    setFormData({
      ...formData,
      examResults: [...formData.examResults, newExam],
    });
  };

  const removeExamResult = (id: string) => {
    setFormData({
      ...formData,
      examResults: formData.examResults.filter((exam) => exam.id !== id),
    });
  };

  const updateExamResult = (id: string, updates: Partial<ExamResult>) => {
    setFormData({
      ...formData,
      examResults: formData.examResults.map((exam) =>
        exam.id === id ? { ...exam, ...updates } : exam
      ),
    });
  };

  const updateSubjectResult = (
    examId: string,
    subject: string,
    grade: string,
    isCompulsory: boolean
  ) => {
    setFormData({
      ...formData,
      examResults: formData.examResults.map((exam) =>
        exam.id === examId
          ? {
            ...exam,
            results: exam.results.map((result) =>
              result.subject === subject && result.isCompulsory === isCompulsory
                ? { ...result, grade }
                : result
            ),
          }
          : exam
      ),
    });
  };

  const removeSubjectResult = (examId: string, subject: string) => {
    setFormData({
      ...formData,
      examResults: formData.examResults.map((exam) =>
        exam.id === examId
          ? {
            ...exam,
            results: exam.results.filter((result) => result.subject !== subject),
          }
          : exam
      ),
    });
  };

  // Check if a step has any validation errors
  const hasStepErrors = (stepId: number): boolean => {
    const stepErrorFields: Record<number, string[]> = {
      0: ['fullName', 'phone', 'dateOfBirth', 'gender', 'nationality'],
      1: ['tuitionType'],
      2: ['preferredState', 'preferredDistricts', 'maxTravelDistance'],
      3: ['subjectCards'],
      4: ['highestEducationLevel', 'otherEducationLevel', 'university', 'fieldOfStudy', 'teachingLanguages', 'customLanguage', 'hourlyRate'],
      5: ['weeklySchedule', 'availability'],
      6: ['hourlyRate'],
      7: ['yearsOfTuitionExperience', 'tutorExperienceTypes'],
      8: ['examResults'],
      9: [''],
      10: ['aboutMe'],
    };

    const fieldsForStep = stepErrorFields[stepId] || [];
    return fieldsForStep.some(field => errors[field]);
  };

  const isTeacherRelatedExperience = hasTeacherRelatedExperience(formData.tutorExperienceTypes);
  const lockedSectionClassName = isVerifiedTutor ? 'opacity-60 pointer-events-none select-none' : '';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{localizeText(locale, 'Edit Profile')}</h1>
          <p className="text-lg text-gray-600">{localizeText(locale, 'Update your tutor profile information')}</p>
        </div>

        {/* Verification Status Banners */}
        {profile?.verificationStatus === 'pending' && (
          <div className="mb-8 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-700">{localizeText(locale, 'Pending Verification')}</p>
              <p className="text-sm text-amber-600 mt-1">{localizeText(locale, "Your profile is currently under review by our team. This typically takes 2-3 business days. You can continue editing your profile while it's being reviewed.")}</p>
            </div>
          </div>
        )}

        {profile?.verificationStatus === 'rejected' && (
          <div className="mb-8 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-700">{localizeText(locale, 'Verification Rejected')}</p>
              {profile.verificationRejectionReason && (
                <p className="text-sm text-red-600 mt-1">{profile.verificationRejectionReason}</p>
              )}
              <p className="text-xs text-red-500 mt-2">{localizeText(locale, 'Please update your profile based on the reason above and resubmit for review.')}</p>
            </div>
          </div>
        )}

        {isVerifiedTutor && (
          <div className="mb-8 flex items-start gap-3 p-4 bg-gray-100 border border-gray-300 rounded-lg">
            <AlertCircle className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-gray-800">{localizeText(locale, 'Verified Profile Locked')}</p>
              <p className="text-sm text-gray-600 mt-1">
                {localizeText(locale, 'Your profile has been verified. Only your subjects and levels can still be updated here.')}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {localizeText(locale, 'We recently updated the subject options, so verified tutors can now adjust their teaching subjects and education levels here when needed.')}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {localizeText(locale, 'Profile photo changes are saved immediately after upload. For any other profile changes, please contact customer service.')}
              </p>
            </div>
          </div>
        )}

        {/* Avatar Upload Card */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
          <div className="px-6 py-5 flex items-center gap-6">
            {/* Avatar preview */}
            <div className="relative flex-shrink-0">
              <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-gray-200 overflow-hidden flex items-center justify-center">
                {isUploadingAvatar ? (
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                ) : (localAvatarUrl || profile?.profilePhotoUrl || profile?.profilePicture) ? (
                  <img
                    src={localAvatarUrl || profile?.profilePhotoUrl || profile?.profilePicture || ''}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-bold text-gray-400">
                    {profile?.fullName?.[0]?.toUpperCase() || '?'}
                  </span>
                )}
              </div>
              <label
                htmlFor="tutor-avatar-upload"
                className="absolute -bottom-1 -right-1 w-7 h-7 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors shadow"
              >
                <Camera className="w-3.5 h-3.5 text-white" />
              </label>
            </div>

            {/* Text + hidden input */}
            <div>
              <p className="font-semibold text-gray-900">{localizeText(locale, 'Profile Photo')}</p>
              <p className="text-sm text-gray-500 mt-0.5">{localizeText(locale, 'JPG, PNG or WebP · max 5 MB')}</p>
              <label
                htmlFor="tutor-avatar-upload"
                className="mt-2 inline-block text-sm text-primary font-medium cursor-pointer hover:underline"
              >
                {localizeText(locale, 'Change photo')}
              </label>
            </div>

            <input
              id="tutor-avatar-upload"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                e.target.value = '';
                setIsUploadingAvatar(true);
                try {
                  const result = await uploadTutorFile('avatar', [file]) as { photoUrl: string };
                  setLocalAvatarUrl(result.photoUrl);
                  // Persist updated avatar into context & localStorage
                  if (profile) {
                    updateProfile({
                      ...profile,
                      profilePhotoUrl: result.photoUrl,
                      profilePicture: result.photoUrl,
                    });
                  }
                  toast.success(localizeText(locale, 'Profile photo updated!'));
                } catch (err) {
                  logger.error('EditTutorProfilePage', 'Failed to upload photo', err);
                  captureError('EditTutor:photoUpload', err);
                  toast.error(localizeText(locale, 'Failed to upload photo. Please try again.'));
                } finally {
                  setIsUploadingAvatar(false);
                }
              }}
            />
          </div>
        </div>

        {/* Form Sections */}
        <div className="space-y-4">
          {/* Step 0: Personal Information */}
          <div className={`bg-white rounded-lg overflow-hidden transition-colors ${
            hasStepErrors(0) 
              ? 'border-2 border-red-500 shadow-md shadow-red-200' 
              : 'border border-gray-200'
          }`}>
            <button
              onClick={() => toggleStep(0)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-primary bg-primary-50 w-8 h-8 rounded-full flex items-center justify-center">
                  1
                </span>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">{steps[0].label}</h3>
                  <p className="text-sm text-gray-500">{localizeText(locale, 'Edit your basic personal information')}</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${expandedSteps.has(0) ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {expandedSteps.has(0) && (
              <div className={`px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-4 ${lockedSectionClassName}`}>
                <p className="text-sm text-gray-600">{localizeText(locale, 'Update your personal information')}</p>

                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    {localizeText(locale, 'Full Name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    placeholder={localizeText(locale, 'Enter your full name')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  {errors.fullName && <ErrorMessage fieldName="fullName" />}
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    {localizeText(locale, 'Phone Number')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder={localizeText(locale, 'Enter your phone number')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  {errors.phone && <ErrorMessage fieldName="phone" />}
                </div>

                {/* Date of Birth */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    {localizeText(locale, 'Date of Birth')} <span className="text-red-500">*</span>
                  </label>
                  <BirthdayPicker
                    value={formData.dateOfBirth}
                    onChange={(val) => setFormData({ ...formData, dateOfBirth: val })}
                    error={!!errors.dateOfBirth}
                  />
                  {errors.dateOfBirth && <ErrorMessage fieldName="dateOfBirth" />}
                </div>

                {/* Gender */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    {localizeText(locale, 'Gender')} <span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    options={GENDER_OPTIONS}
                    value={formData.gender}
                    onChange={(value) => setFormData({ ...formData, gender: value as string })}
                    placeholder={localizeText(locale, 'Select your gender')}
                    searchable={false}
                  />
                  {errors.gender && <ErrorMessage fieldName="gender" />}
                </div>

                {/* Nationality */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    {localizeText(locale, 'Nationality')} <span className="text-red-500">*</span>
                  </label>
                  <SearchableInput
                    options={NATIONALITIES}
                    value={formData.nationality}
                    onChange={(value) => setFormData({ ...formData, nationality: value })}
                    placeholder={localizeText(locale, 'Search and select your nationality')}
                    strictSelection={true}
                  />
                  {errors.nationality && <ErrorMessage fieldName="nationality" />}
                </div>
              </div>
            )}
          </div>

          {/* Step 1: Tuition Type */}
          <div className={`bg-white rounded-lg overflow-hidden transition-colors ${
            hasStepErrors(1) 
              ? 'border-2 border-red-500 shadow-md shadow-red-200' 
              : 'border border-gray-200'
          }`}>
            <button
              onClick={() => toggleStep(1)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-primary bg-primary-50 w-8 h-8 rounded-full flex items-center justify-center">
                  2
                </span>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">{steps[1].label}</h3>
                  <p className="text-sm text-gray-500">{localizeText(locale, 'Select tuition types you offer')}</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${expandedSteps.has(1) ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {expandedSteps.has(1) && (
              <div className={`px-6 py-4 border-t border-gray-200 bg-gray-50 ${lockedSectionClassName}`}>
                <p className="text-sm text-gray-600 mb-6">{localizeText(locale, 'Select all the types of tuition you can provide')}</p>
                {errors.tuitionType && <ErrorMessage fieldName="tuitionType" />}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button
                    onClick={() => {
                      setFormData({
                        ...formData,
                        tuitionType:
                          formData.tuitionType === 'physical'
                            ? ''
                            : formData.tuitionType === 'both'
                              ? 'online'
                              : 'physical',
                      });
                    }}
                    className={`flex min-h-[11rem] flex-col items-center justify-center gap-3 rounded-lg border-2 px-4 py-6 font-medium text-center transition-all ${formData.tuitionType === 'physical' || formData.tuitionType === 'both'
                        ? 'border-secondary bg-secondary-50 text-secondary'
                        : 'border-gray-300 hover:border-secondary text-gray-700'
                      }`}
                  >
                    <TuitionTypeOptionContent
                      type="physical"
                      label={localizeText(locale, 'Physical Tuition')}
                      showSelectedIndicator={formData.tuitionType === 'physical' || formData.tuitionType === 'both'}
                      selectedLabel={localizeText(locale, 'Selected')}
                    />
                  </button>

                  <button
                    onClick={() => {
                      setFormData({
                        ...formData,
                        tuitionType:
                          formData.tuitionType === 'online'
                            ? ''
                            : formData.tuitionType === 'both'
                              ? 'physical'
                              : 'online',
                      });
                    }}
                    className={`flex min-h-[11rem] flex-col items-center justify-center gap-3 rounded-lg border-2 px-4 py-6 font-medium text-center transition-all ${formData.tuitionType === 'online' || formData.tuitionType === 'both'
                        ? 'border-secondary bg-secondary-50 text-secondary'
                        : 'border-gray-300 hover:border-secondary text-gray-700'
                      }`}
                  >
                    <TuitionTypeOptionContent
                      type="online"
                      label={localizeText(locale, 'Online Tuition')}
                      showSelectedIndicator={formData.tuitionType === 'online' || formData.tuitionType === 'both'}
                      selectedLabel={localizeText(locale, 'Selected')}
                    />
                  </button>

                  <button
                    onClick={() => {
                      setFormData({
                        ...formData,
                        tuitionType: formData.tuitionType === 'both' ? '' : 'both',
                      });
                    }}
                    className={`flex min-h-[11rem] flex-col items-center justify-center gap-3 rounded-lg border-2 px-4 py-6 font-medium text-center transition-all ${formData.tuitionType === 'both'
                        ? 'border-secondary bg-secondary-50 text-secondary'
                        : 'border-gray-300 hover:border-secondary text-gray-700'
                      }`}
                  >
                    <TuitionTypeOptionContent
                      type="both"
                      label={localizeText(locale, 'Both')}
                      showSelectedIndicator={formData.tuitionType === 'both'}
                      selectedLabel={localizeText(locale, 'Selected')}
                    />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Step 2: Teaching Areas */}
          <div className={`bg-white rounded-lg overflow-hidden transition-colors ${
            hasStepErrors(2) 
              ? 'border-2 border-red-500 shadow-md shadow-red-200' 
              : 'border border-gray-200'
          }`}>
            <button
              onClick={() => toggleStep(2)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-primary bg-primary-50 w-8 h-8 rounded-full flex items-center justify-center">
                  3
                </span>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">{steps[2].label}</h3>
                  <p className="text-sm text-gray-500">{localizeText(locale, 'Select your preferred teaching areas')}</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${expandedSteps.has(2) ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {expandedSteps.has(2) && (
              <div className={`px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-4 ${lockedSectionClassName}`}>
                <p className="text-sm text-gray-600">{localizeText(locale, 'Select your preferred teaching state and districts')}</p>

                {/* State, Districts, Area Notes — hidden for online */}
                {formData.tuitionType !== 'online' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {localizeText(locale, 'State')} <span className="text-red-500">*</span>
                    </label>
                    <SearchableDropdown
                      options={STATE_OPTIONS}
                      value={formData.preferredState}
                      onChange={(value) => {
                        setFormData({
                          ...formData,
                          preferredState: String(value),
                          preferredDistricts: [],
                          preferredAreaNotes: [],
                        });
                      }}
                      placeholder={localizeText(locale, 'Select a state...')}
                      searchable={true}
                    />
                  </div>
                )}

                {/* Districts Selection (only if state is selected and not online) */}
                {formData.preferredState && formData.tuitionType !== 'online' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {localizeText(locale, 'Preferred Districts (Select multiple)')}
                    </label>
                    {errors.preferredDistricts && <ErrorMessage fieldName="preferredDistricts" />}
                    <MultiSelect
                      options={STATE_DISTRICTS[formData.preferredState as keyof typeof STATE_DISTRICTS] || []}
                      value={formData.preferredDistricts}
                      onChange={(districts) => setFormData({ ...formData, preferredDistricts: districts })}
                      placeholder={localizeText(locale, 'Select districts...')}
                      displayMode="count"
                      searchable={true}
                      showSelected={true}
                    />
                    {formData.preferredDistricts.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.preferredDistricts.map((d) => (
                          <span
                            key={d}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-secondary/10 text-secondary text-sm rounded-md border border-secondary/20"
                          >
                            {d}
                            <button
                              type="button"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  preferredDistricts: formData.preferredDistricts.filter((x) => x !== d),
                                })
                              }
                              className="hover:text-secondary"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Area Notes */}
                {formData.preferredState && formData.tuitionType !== 'online' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {localizeText(locale, 'Specific Areas / Neighbourhoods')} <span className="text-gray-400 font-normal">({localizeText(locale, 'Optional')})</span>
                    </label>
                    <AreaNoteInputEdit
                      isZh={isZh}
                      onAdd={(note) => {
                        if (note && !formData.preferredAreaNotes.includes(note)) {
                          setFormData({ ...formData, preferredAreaNotes: [...formData.preferredAreaNotes, note] });
                        }
                      }}
                    />
                    {formData.preferredAreaNotes.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.preferredAreaNotes.map((note) => (
                          <span
                            key={note}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-accent/10 text-accent-700 text-sm rounded-md border border-accent/20"
                          >
                            {note}
                            <button
                              type="button"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  preferredAreaNotes: formData.preferredAreaNotes.filter((x) => x !== note),
                                })
                              }
                              className="hover:text-accent-800"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Step 3: Subjects by Level */}
          <div className={`bg-white rounded-lg overflow-hidden transition-colors ${
            hasStepErrors(3) 
              ? 'border-2 border-red-500 shadow-md shadow-red-200' 
              : 'border border-gray-200'
          }`}>
            <button
              onClick={() => toggleStep(3)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-primary bg-primary-50 w-8 h-8 rounded-full flex items-center justify-center">
                  4
                </span>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">{steps[3].label}</h3>
                  <p className="text-sm text-gray-500">{localizeText(locale, 'Select subjects you teach by education level')}</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${expandedSteps.has(3) ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {expandedSteps.has(3) && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-4">
                {errors.subjectCards && <ErrorMessage fieldName="subjectCards" />}
                {formData.subjectCards.map((card) => (
                  <Card key={card.id} className="bg-white">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">{localizeText(locale, 'Education System')}</label>
                        <SearchableDropdown
                          options={EDUCATION_SYSTEM_OPTIONS}
                          value={(card as any).educationSystem}
                          onChange={(value) => updateSubjectCard(card.id, {
                            educationSystem: String(value),
                            educationLevels: [],
                            subjects: [],
                          })}
                          placeholder={localizeText(locale, 'Select education system')}
                          searchable={true}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">{localizeText(locale, 'Education Level(s)')}</label>
                        <LevelMultiSelect
                          value={(card as any).educationLevels as EducationLevel[]}
                          onChange={(levels) => updateSubjectCard(card.id, { educationLevels: levels, subjects: [] })}
                          placeholder={localizeText(locale, 'Select education level(s)')}
                          disabled={!(card as any).educationSystem}
                          systemId={(card as any).educationSystem}
                          optionLabels={localizedEducationLevelLabels}
                        />
                      </div>

                      {(card as any).educationLevels && (card as any).educationLevels.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">
                            {isZh ? '科目：' : 'Subjects for '} {(card as any).educationLevels.map((l: EducationLevel) => localizeText(locale, EDUCATION_LEVEL_LABELS[l])).join(isZh ? '、' : ', ')}
                          </label>
                          <MultiSelect
                            options={getSubjectsForLevels((card as any).educationLevels as EducationLevel[])}
                            value={card.subjects}
                            onChange={(subjects) => updateSubjectCard(card.id, { subjects })}
                            placeholder={localizeText(locale, 'Select subjects')}
                          />
                        </div>
                      )}

                      <button
                        onClick={() => removeSubjectCard(card.id)}
                        className="text-red-600 hover:text-red-700 flex items-center gap-2 text-sm font-medium"
                      >
                        <Trash2 className="w-4 h-4" />
                        {localizeText(locale, 'Remove')}
                      </button>
                    </div>
                  </Card>
                ))}

                <button
                  onClick={addSubjectCard}
                  className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-700 hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {localizeText(locale, 'Add Subject')}
                </button>
              </div>
            )}
          </div>

          {/* Step 4: Education */}
          <div className={`bg-white rounded-lg overflow-hidden transition-colors ${
            hasStepErrors(4) 
              ? 'border-2 border-red-500 shadow-md shadow-red-200' 
              : 'border border-gray-200'
          }`}>
            <button
              onClick={() => toggleStep(4)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-primary bg-primary-50 w-8 h-8 rounded-full flex items-center justify-center">
                  5
                </span>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">{steps[4].label}</h3>
                  <p className="text-sm text-gray-500">{localizeText(locale, 'Enter your education information')}</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${expandedSteps.has(4) ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {expandedSteps.has(4) && (
              <div className={`px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-6 ${lockedSectionClassName}`}>
                {/* Current Status - Tab Style */}
                <div>
                  <TabButtonGroup
                    options={localizedCurrentStatusOptions}
                    value={formData.currentStatus}
                    onChange={(value) => setFormData({ ...formData, currentStatus: value as string })}
                    label={localizeText(locale, 'Current Status')}
                    isRequired={true}
                  />
                  {errors.currentStatus && <ErrorMessage fieldName="currentStatus" />}
                </div>

                {/* Highest Education Level - Tab Style */}
                <div>
                  <TabButtonGroup
                    options={localizedEducationLevelsHighestOptions}
                    value={formData.highestEducationLevel}
                    onChange={(value) => setFormData({ ...formData, highestEducationLevel: value as string })}
                    label={localizeText(locale, 'Highest Education Level')}
                    isRequired={true}
                  />
                  {errors.highestEducationLevel && <ErrorMessage fieldName="highestEducationLevel" />}
                  
                  {formData.highestEducationLevel === 'others' && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-900 mb-2">{localizeText(locale, 'Please specify')}</label>
                      <input
                        type="text"
                        value={formData.otherEducationLevel}
                        onChange={(e) => setFormData({ ...formData, otherEducationLevel: e.target.value })}
                        className={`input-field ${errors.otherEducationLevel ? 'border-red-500' : ''}`}
                        placeholder={localizeText(locale, 'Enter your education level')}
                      />
                      {errors.otherEducationLevel && <ErrorMessage fieldName="otherEducationLevel" />}
                    </div>
                  )}
                </div>

                {/* Teaching Languages */}
                <div>
                  <TabButtonGroup
                    options={localizedTeachingLanguageOptions}
                    value={formData.teachingLanguages}
                    onChange={(value) => {
                      const languages = value as string[];
                      setFormData({ ...formData, teachingLanguages: languages });
                    }}
                    label={localizeText(locale, 'Teaching Languages')}
                    isRequired={true}
                    multiple={true}
                  />
                  {errors.teachingLanguages && <ErrorMessage fieldName="teachingLanguages" />}

                  {formData.teachingLanguages.includes('other') && (
                    <div className="mt-4">
                      <input
                        type="text"
                        placeholder={isZh ? '例如：法语、德语、日语、韩语...' : 'e.g., French, German, Japanese, Korean...'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        value={formData.customLanguage}
                        onChange={(e) => setFormData({ ...formData, customLanguage: e.target.value })}
                      />
                      {errors.customLanguage && <ErrorMessage fieldName="customLanguage" />}
                    </div>
                  )}
                </div>

                {/* School with University Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    {formData.currentStatus === 'studying'
                      ? localizeText(locale, 'Current Studying University / School')
                      : localizeText(locale, 'School')} <span className="text-red-500">*</span>
                  </label>
                  <SearchableInput
                    options={UNIVERSITIES}
                    value={formData.school}
                    onChange={(value) => setFormData({ ...formData, school: String(value) })}
                    placeholder={localizeText(locale, 'Search for a university or enter manually...')}
                  />
                  {errors.university && <ErrorMessage fieldName="university" />}
                </div>

                {/* Major of Study */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    {localizeText(locale, 'Major of Study')} <span className="text-red-500">*</span>
                  </label>
                  <SearchableInput
                    options={MAJOR_OPTIONS}
                    value={formData.major}
                    onChange={(value) => setFormData({ ...formData, major: String(value) })}
                    placeholder={localizeText(locale, 'Search for a field of study or enter manually...')}
                  />
                  {errors.fieldOfStudy && <ErrorMessage fieldName="fieldOfStudy" />}
                </div>
              </div>
            )}
          </div>

          {/* Step 5: Teaching Time */}
          <div className={`bg-white rounded-lg overflow-hidden transition-colors ${
            hasStepErrors(5) 
              ? 'border-2 border-red-500 shadow-md shadow-red-200' 
              : 'border border-gray-200'
          }`}>
            <button
              onClick={() => toggleStep(5)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-primary bg-primary-50 w-8 h-8 rounded-full flex items-center justify-center">
                  6
                </span>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">{steps[5].label}</h3>
                  <p className="text-sm text-gray-500">{localizeText(locale, 'Set your teaching availability')}</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${expandedSteps.has(5) ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {expandedSteps.has(5) && (
              <div className={`px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-4 ${lockedSectionClassName}`}>
                {errors.weeklySchedule && <ErrorMessage fieldName="weeklySchedule" />}
                <WeeklyTimeTable
                  selectedTimes={formData.availability}
                  onChange={(schedule) => setFormData({ ...formData, availability: schedule })}
                  {...weeklyTimeTableI18n}
                />
              </div>
            )}
          </div>

          {/* Step 6: Hourly Rate */}
          <div className={`bg-white rounded-lg overflow-hidden transition-colors ${
            hasStepErrors(6) 
              ? 'border-2 border-red-500 shadow-md shadow-red-200' 
              : 'border border-gray-200'
          }`}>
            <button
              onClick={() => toggleStep(6)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-primary bg-primary-50 w-8 h-8 rounded-full flex items-center justify-center">
                    7
                  </span>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">{steps[6].label}</h3>
                  <p className="text-sm text-gray-500">{localizeText(locale, 'Set your hourly rate')}</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${expandedSteps.has(6) ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {expandedSteps.has(6) && (
              <div className={`px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-6 ${lockedSectionClassName}`}>
                {errors.hourlyRate && <ErrorMessage fieldName="hourlyRate" />}
                <SingleRangeSlider
                  label={localizeText(locale, 'Hourly Rate (MYR)')}
                  min={10}
                  max={300}
                  value={formData.hourlyRate ? Number(formData.hourlyRate) : 50}
                  onChange={(value) => setFormData({ ...formData, hourlyRate: value.toString() })}
                  disabled={isVerifiedTutor}
                />
              </div>
            )}
          </div>

          {/* Step 7: Tutor Experience */}
          <div className={`bg-white rounded-lg overflow-hidden transition-colors ${
            hasStepErrors(7) 
              ? 'border-2 border-red-500 shadow-md shadow-red-200' 
              : 'border border-gray-200'
          }`}>
            <button
              onClick={() => toggleStep(7)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-primary bg-primary-50 w-8 h-8 rounded-full flex items-center justify-center">
                  8
                </span>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">{steps[7].label}</h3>
                  <p className="text-sm text-gray-500">{localizeText(locale, 'Tell us about your tutoring experience')}</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${expandedSteps.has(7) ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {expandedSteps.has(7) && (
              <div className={`px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-4 ${lockedSectionClassName}`}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{localizeText(locale, 'Years of Experience')}</label>
                  {errors.yearsOfTuitionExperience && <ErrorMessage fieldName="yearsOfTuitionExperience" />}
                  <SearchableDropdown
                    options={TUITION_EXPERIENCE_YEARS.map(opt => ({ value: String(opt.value), label: opt.label }))}
                    value={formData.yearsOfTuitionExperience !== undefined && formData.yearsOfTuitionExperience !== '' ? String(formData.yearsOfTuitionExperience) : ''}
                    onChange={(value) => {
                      setFormData({
                        ...formData,
                        yearsOfTuitionExperience: Number(value),
                      });
                    }}
                    placeholder={localizeText(locale, 'Select years')}
                    disabled={formData.tutorExperienceTypes.includes('No experience')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{localizeText(locale, 'Types of Experience')}</label>
                  <p className="text-sm text-gray-600 mb-4">{localizeText(locale, 'Select all that apply:')}</p>
                  {errors.tutorExperienceTypes && <ErrorMessage fieldName="tutorExperienceTypes" />}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {TUTOR_EXPERIENCE_TYPES.map((type) => {
                      const isNoExpSelected = formData.tutorExperienceTypes.includes('No experience');
                      const isDisabled = isNoExpSelected && type !== 'No experience';
                      return (
                        <button
                          key={type}
                          type="button"
                          disabled={isDisabled}
                          onClick={() => {
                            const isAdding = !formData.tutorExperienceTypes.includes(type);

                            if (type === 'No experience') {
                              setFormData({
                                ...formData,
                                tutorExperienceTypes: isAdding ? ['No experience'] : [],
                                yearsOfTuitionExperience: isAdding ? '' : formData.yearsOfTuitionExperience,
                                examResults: [],
                              });
                              return;
                            }

                            let newTypes = isAdding
                              ? [...formData.tutorExperienceTypes.filter((t) => t !== 'No experience'), type]
                              : formData.tutorExperienceTypes.filter((t) => t !== type);

                            // Mutual exclusivity: Current vs Retired School Teacher
                            if (type === 'Current School Teacher' && isAdding) {
                              newTypes = newTypes.filter((t) => t !== 'Retired School Teacher');
                            } else if (type === 'Retired School Teacher' && isAdding) {
                              newTypes = newTypes.filter((t) => t !== 'Current School Teacher');
                            }

                            // Mutual exclusivity: Part-time vs Full-time Tuition Center Tutor
                            if (type === 'Part-time Tuition Center Tutor' && isAdding) {
                              newTypes = newTypes.filter((t) => t !== 'Full-time Tuition Center Tutor');
                            } else if (type === 'Full-time Tuition Center Tutor' && isAdding) {
                              newTypes = newTypes.filter((t) => t !== 'Part-time Tuition Center Tutor');
                            }

                            setFormData({
                              ...formData,
                              tutorExperienceTypes: newTypes,
                              examResults: hasTeacherRelatedExperience(newTypes) ? [] : formData.examResults,
                            });
                          }}
                          className={`px-4 py-2 rounded-full font-medium text-sm transition-colors ${
                            formData.tutorExperienceTypes.includes(type)
                              ? 'bg-accent text-white'
                              : isDisabled
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {localizeText(locale, type)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {isTeacherRelatedExperience && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {localizeText(locale, 'School / Teaching Institution')} <span className="text-gray-400">({localizeText(locale, 'optional')})</span>
                    </label>
                    <p className="text-sm text-gray-600 mb-3">
                      {localizeText(locale, 'Sharing the school or institution where you teach can significantly improve your expected hourly rate and selection chances.')}
                    </p>
                    <input
                      type="text"
                      value={formData.teachingInstitution}
                      onChange={(e) => setFormData({ ...formData, teachingInstitution: e.target.value })}
                      placeholder={localizeText(locale, 'e.g. SMK Taman Desa, ABC Kindergarten')}
                      className="input-field"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Step 8: Public Exam Results */}
          {!isTeacherRelatedExperience && (
          <div className={`bg-white rounded-lg overflow-hidden transition-colors ${
            hasStepErrors(8) 
              ? 'border-2 border-red-500 shadow-md shadow-red-200' 
              : 'border border-gray-200'
          }`}>
            <button
              onClick={() => toggleStep(8)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-primary bg-primary-50 w-8 h-8 rounded-full flex items-center justify-center">
                  9
                </span>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">{steps[8].label}</h3>
                  <p className="text-sm text-gray-500">{localizeText(locale, 'Enter your public exam results')}</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${expandedSteps.has(8) ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {expandedSteps.has(8) && (
              <div className={`px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-4 ${lockedSectionClassName}`}>
                {errors.examResults && <ErrorMessage fieldName="examResults" />}
                <PastPublicExamResults
                  examResults={formData.examResults}
                  onAddExamResult={addExamResult}
                  onRemoveExamResult={removeExamResult}
                  onUpdateExamResult={updateExamResult}
                  onUpdateSubjectResult={updateSubjectResult}
                  onRemoveSubjectResult={removeSubjectResult}
                />

                <button
                  onClick={addExamResult}
                  className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-700 hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {localizeText(locale, 'Add Exam Result')}
                </button>
              </div>
            )}
          </div>
          )}

          {/* Step 9: Teaching Style */}
          <div className={`bg-white rounded-lg overflow-hidden transition-colors ${
            hasStepErrors(9) 
              ? 'border-2 border-red-500 shadow-md shadow-red-200' 
              : 'border border-gray-200'
          }`}>
            <button
              onClick={() => toggleStep(9)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-primary bg-primary-50 w-8 h-8 rounded-full flex items-center justify-center">
                  10
                </span>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">{steps[9].label}</h3>
                  <p className="text-sm text-gray-500">{localizeText(locale, 'Select your teaching styles')}</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${expandedSteps.has(9) ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {expandedSteps.has(9) && (
              <div className={`px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-6 ${lockedSectionClassName}`}>
                <h3 className="text-lg font-semibold text-gray-900">{localizeText(locale, 'Your Teaching Style')}</h3>

                {/* I am */}
                <TabButtonGroup
                  options={localizedTeachingStyleIAmOptions}
                  value={Array.isArray(formData.teachingStyle) ? [] : formData.teachingStyle?.iAm || []}
                  onChange={(value) => {
                    const currentStyle = Array.isArray(formData.teachingStyle) ? defaultTeachingStyle : (formData.teachingStyle || defaultTeachingStyle);
                    setFormData({
                      ...formData,
                      teachingStyle: { ...currentStyle, iAm: value as string[] },
                    });
                  }}
                  label={localizeText(locale, 'I am')}
                  multiple={true}
                />

                {/* I can */}
                <TabButtonGroup
                  options={localizedTeachingStyleICanOptions}
                  value={Array.isArray(formData.teachingStyle) ? [] : formData.teachingStyle?.iCan || []}
                  onChange={(value) => {
                    const currentStyle = Array.isArray(formData.teachingStyle) ? defaultTeachingStyle : (formData.teachingStyle || defaultTeachingStyle);
                    setFormData({
                      ...formData,
                      teachingStyle: { ...currentStyle, iCan: value as string[] },
                    });
                  }}
                  label={localizeText(locale, 'I can')}
                  multiple={true}
                />

                {/* I know */}
                <TabButtonGroup
                  options={localizedTeachingStyleIKnowOptions}
                  value={Array.isArray(formData.teachingStyle) ? [] : formData.teachingStyle?.iKnow || []}
                  onChange={(value) => {
                    const currentStyle = Array.isArray(formData.teachingStyle) ? defaultTeachingStyle : (formData.teachingStyle || defaultTeachingStyle);
                    setFormData({
                      ...formData,
                      teachingStyle: { ...currentStyle, iKnow: value as string[] },
                    });
                  }}
                  label={localizeText(locale, 'I know')}
                  multiple={true}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">{localizeText(locale, 'Others (Optional)')}</label>
                  <input
                    type="text"
                    placeholder={localizeText(locale, 'Add any other teaching characteristics...')}
                    className="input-field"
                    value={Array.isArray(formData.teachingStyle) ? '' : formData.teachingStyle?.others || ''}
                    onChange={(e) => {
                      const currentStyle = Array.isArray(formData.teachingStyle) ? defaultTeachingStyle : (formData.teachingStyle || defaultTeachingStyle);
                      setFormData({
                        ...formData,
                        teachingStyle: { ...currentStyle, others: e.target.value },
                      });
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Step 10: About Me */}
          <div className={`bg-white rounded-lg overflow-hidden transition-colors ${
            hasStepErrors(10) 
              ? 'border-2 border-red-500 shadow-md shadow-red-200' 
              : 'border border-gray-200'
          }`}>
            <button
              onClick={() => toggleStep(10)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-primary bg-primary-50 w-8 h-8 rounded-full flex items-center justify-center">
                  11
                </span>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">{steps[10].label}</h3>
                  <p className="text-sm text-gray-500">{localizeText(locale, 'Write about yourself')}</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${expandedSteps.has(10) ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {expandedSteps.has(10) && (
              <div className={`px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-4 ${lockedSectionClassName}`}>
                {errors.aboutMe && <ErrorMessage fieldName="aboutMe" />}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">{localizeText(locale, 'About Me')}</label>
                  <textarea
                    value={formData.aboutMe}
                    onChange={(e) => setFormData({ ...formData, aboutMe: e.target.value })}
                    placeholder={localizeText(locale, 'Tell us about yourself, your teaching philosophy, and experience...')}
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-12 flex justify-end gap-4 sticky bottom-0 z-sticky bg-gradient-to-t from-gray-100 to-transparent pt-8 pb-4">
          <button
            onClick={() => navigate(-1)}
            className="px-8 py-3 rounded-lg font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            {localizeText(locale, 'Cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            className={`px-8 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all ${isSubmitting
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-primary hover:bg-primary-600 text-white shadow-lg hover:shadow-xl'
              }`}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {localizeText(locale, 'Saving...')}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isVerifiedTutor ? localizeText(locale, 'Save Subjects & Levels') : localizeText(locale, 'Save Changes')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditTutorProfilePage;
